import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronRight, Filter, Download, Search, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOpsDashboard } from '@/context/OpsDashboardContext';

interface SupplySummary {
  total_eligible_projects: number;
  total_slots: number;
  total_slots_by_vertical: Record<string, number>;
  assigned_slots: number;
  available_slots: number;
  overall_utilization: number;
  total_projected_audience: number;
  clearable_projects: number;
  constrained_projects: number;
  effective_capacity: number;
  total_revenue_at_full_util: number;
  assigned_revenue: number;
}

interface Show {
  id: string;
  show_name: string;
  creator: string;
  vertical: string;
  artie_score: number;
  production_status: string;
  forecasted_reach_90d: number;
  episodes_total: number;
  episodes_uploaded: number;
  total_slots_actual?: number;
  total_slots_extrapolated?: number;
  assigned_slots?: number;
  available_slots?: number;
  utilization?: number;
  creator_floor_ecpm: number;
  floor_status?: 'clearable' | 'constrained';
  assigned_brands?: string[];
  competitive_categories?: string[];
  creator_brand_preferences: string[];
}

interface Slot {
  id: string;
  show_name: string;
  episode: string;
  slot_type: string;
  rate: number;
  status: string;
  assigned_brand?: string;
  integration_context?: string;
}

interface SupplyViewResponse {
  summary: SupplySummary;
  shows: Show[];
  total: number;
  page: number;
  limit: number;
}

const PAGE_SIZE = 15;

const SupplyViewPage: React.FC = () => {
  const [data, setData] = useState<SupplyViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchSeqRef = useRef(0);
  const [expandedShows, setExpandedShows] = useState<Set<string>>(new Set());
  const [showSlots, setShowSlots] = useState<Record<string, Slot[]>>({});
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    vertical: '',
    production_status: '',
    artie_score_min: '',
    artie_score_max: '',
    has_available_slots: '',
    floor_status: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('artie_score');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { toast } = useToast();
  const { refreshTriggers } = useOpsDashboard();

  const fetchSupplyData = async () => {
    const isInitialLoad = !data;
    const normalizedSearch = filters.search.trim();
    const fetchSeq = ++fetchSeqRef.current;
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsFetching(true);
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const params = new URLSearchParams({
        page: page.toString(),
        limit: PAGE_SIZE.toString(),
        ...(filters.vertical && filters.vertical !== 'all' && { vertical: filters.vertical }),
        ...(filters.production_status && filters.production_status !== 'all' && { production_status: filters.production_status }),
        ...(filters.artie_score_min && { artie_score_min: filters.artie_score_min }),
        ...(filters.artie_score_max && { artie_score_max: filters.artie_score_max }),
        ...(filters.has_available_slots && filters.has_available_slots !== 'all' && { has_available_slots: filters.has_available_slots }),
        ...(filters.floor_status && filters.floor_status !== 'all' && { floor_status: filters.floor_status }),
        ...(normalizedSearch && { search: normalizedSearch })
      });

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/supply?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Failed to fetch supply data');
      }

      const result = await response.json();
      if (fetchSeq === fetchSeqRef.current) {
        setData(result);
      }
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      console.error('Error fetching supply data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch supply data",
        variant: "destructive"
      });
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setIsFetching(false);
        }
      }
    }
  };

  const fetchShowSlots = async (showId: string) => {
    // Safety check to prevent undefined showId
    if (!showId || showId === 'undefined') {
      return;
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/supply/${showId}/slots`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch show slots');
      }

      const slots = await response.json();
      setShowSlots(prev => ({ ...prev, [showId]: slots }));
    } catch (error) {
      console.error('Error fetching show slots:', error);
      toast({
        title: "Error",
        description: "Failed to fetch show slots",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchSupplyData();
  }, [page, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters.search]);

  // Listen for refresh triggers from the context
  useEffect(() => {
    if (refreshTriggers.supply > 0) {
      fetchSupplyData();
    }
  }, [refreshTriggers.supply]);

  const toggleShowExpansion = (showId: string) => {
    // Safety check to prevent undefined showId
    if (!showId || showId === 'undefined') {
      console.error('Invalid showId:', showId);
      return;
    }

    const newExpanded = new Set(expandedShows);
    if (newExpanded.has(showId)) {
      newExpanded.delete(showId);
    } else {
      newExpanded.add(showId);
      if (!showSlots[showId]) {
        fetchShowSlots(showId);
      }
    }
    setExpandedShows(newExpanded);
  };

  const getUtilizationColor = (utilization?: number) => {
    if (utilization === undefined || utilization === null) return 'bg-gray-100';
    if (utilization >= 75) return 'bg-green-100 text-green-800';
    if (utilization >= 25) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getArtieScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${type} ID copied to clipboard`,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      });
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handleExport = () => {
    if (!data || !data.shows.length) return;

    const headers = [
      'Show Name',
      'Creator',
      'Vertical',
      'Artie Score',
      'Production Status',
      'Forecasted Reach 90d',
      'Episodes Total',
      'Episodes Uploaded',
      'Total Slots',
      'Assigned Slots',
      'Available Slots',
      'Utilization (%)',
      'Creator Floor eCPM',
      'Floor Status',
      'Assigned Brands'
    ];

    const rows = data.shows.map((show) => [
      show.show_name,
      show.creator,
      show.vertical,
      show.artie_score.toString(),
      show.production_status,
      show.forecasted_reach_90d.toString(),
      show.episodes_total.toString(),
      show.episodes_uploaded.toString(),
      (show.total_slots_actual || show.total_slots_extrapolated || Math.max(0, (show.assigned_slots || 0) + (show.available_slots || 0))).toString(),
      (show.assigned_slots ?? 0).toString(),
      (show.available_slots ?? 0).toString(),
      (show.utilization ?? 0).toString(),
      show.creator_floor_ecpm.toString(),
      show.floor_status ?? '',
      (show.assigned_brands ?? []).join('; ')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((field) => {
            if (field.includes(',') || field.includes('"') || field.includes('\n')) {
              return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          })
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'supply-view-shows.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No supply data available</p>
      </div>
    );
  }

  const listTotalRaw = Number(data.total);
  const listTotal =
    Number.isFinite(listTotalRaw) && listTotalRaw >= 0 ? Math.floor(listTotalRaw) : 0;
  const supplyFiltersActive =
    (filters.vertical && filters.vertical !== 'all') ||
    (filters.production_status && filters.production_status !== 'all') ||
    Boolean(filters.artie_score_min) ||
    Boolean(filters.artie_score_max) ||
    (filters.has_available_slots && filters.has_available_slots !== 'all') ||
    (filters.floor_status && filters.floor_status !== 'all') ||
    Boolean(filters.search.trim());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supply View</h1>
          <p className="text-gray-600">Total inventory and utilization overview</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!data?.shows.length}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>
      </div>

      {/* Supply Summary Panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Eligible Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.total_eligible_projects}</div>
            <p className="text-xs text-gray-500">Artie Score ≥ 70</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Slots</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(data.summary.total_slots)}</div>
            <div className="text-xs text-gray-500">
              {Object.entries(data.summary.total_slots_by_vertical).map(([vertical, count]) => (
                <span key={vertical} className="mr-2">{vertical}: {count}</span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.overall_utilization.toFixed(1)}%</div>
            <p className="text-xs text-gray-500">
              {formatNumber(data.summary.assigned_slots)} / {formatNumber(data.summary.total_slots)} slots
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.assigned_revenue)}</div>
            <p className="text-xs text-gray-500">
              of {formatCurrency(data.summary.total_revenue_at_full_util)} potential
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Projected Audience</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(data.summary.total_projected_audience)}</div>
            <p className="text-xs text-gray-500">90-day reach</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Floor Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Clearable: {data.summary.clearable_projects}
              </Badge>
              <Badge variant="secondary" className="bg-red-100 text-red-800">
                Constrained: {data.summary.constrained_projects}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Search</label>
                <Input
                  placeholder="Show name..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Vertical</label>
                <Select value={filters.vertical} onValueChange={(value) => setFilters(prev => ({ ...prev, vertical: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="All verticals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All verticals</SelectItem>
                    <SelectItem value="Tech">Tech</SelectItem>
                    <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                    <SelectItem value="Food & Bev">Food & Bev</SelectItem>
                    <SelectItem value="Auto">Auto</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Sports">Sports</SelectItem>
                    <SelectItem value="Drama">Drama</SelectItem>
                    <SelectItem value="Comedy">Comedy</SelectItem>
                    <SelectItem value="Documentary">Documentary</SelectItem>
                    <SelectItem value="Reality">Reality</SelectItem>
                    <SelectItem value="Crime/Thriller">Crime/Thriller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Production Status</label>
                <Select value={filters.production_status} onValueChange={(value) => setFilters(prev => ({ ...prev, production_status: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="script_review">Script Review</SelectItem>
                    <SelectItem value="pre_production">Pre-Production</SelectItem>
                    <SelectItem value="in_production">In Production</SelectItem>
                    <SelectItem value="post">Post</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Floor Status</label>
                <Select value={filters.floor_status} onValueChange={(value) => setFilters(prev => ({ ...prev, floor_status: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="clearable">Clearable</SelectItem>
                    <SelectItem value="constrained">Constrained</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Available Slots</label>
                <Select value={filters.has_available_slots} onValueChange={(value) => setFilters(prev => ({ ...prev, has_available_slots: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Has Available</SelectItem>
                    <SelectItem value="false">No Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">Min Score</label>
                  <Input
                    type="number"
                    placeholder="70"
                    value={filters.artie_score_min}
                    onChange={(e) => setFilters(prev => ({ ...prev, artie_score_min: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Max Score</label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={filters.artie_score_max}
                    onChange={(e) => setFilters(prev => ({ ...prev, artie_score_max: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Show-Level Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Shows</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Show Name</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead>Vertical</TableHead>
                  <TableHead>Artie Score</TableHead>
                  <TableHead>Production Status</TableHead>
                  <TableHead>Audience (90d)</TableHead>
                  <TableHead>Episodes</TableHead>
                  <TableHead>Slots</TableHead>
                  <TableHead>Utilization</TableHead>
                  <TableHead>Floor eCPM</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned Brands</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.shows.map((show) => {
                  // Debug logging
                  if (!show.id) {
                    console.error('Show missing ID:', show);
                  }

                  return (
                    <React.Fragment key={show.id || `show-${Math.random()}`}>
                      <TableRow className="hover:bg-gray-50">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleShowExpansion(show.id)}
                          >
                            {expandedShows.has(show.id) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{show.show_name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(show.id, 'Show')}
                              className="h-6 w-6 p-0 hover:bg-gray-100"
                              title="Copy Show ID"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{show.creator}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{show.vertical}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getArtieScoreColor(show.artie_score)}>
                            {show.artie_score.toFixed(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{show.production_status.replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell>{formatNumber(show.forecasted_reach_90d)}</TableCell>
                        <TableCell>
                          {show.episodes_uploaded} of {show.episodes_total}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>
                              {showSlots[show.id] ? (
                                `${showSlots[show.id].length} total`
                              ) : (
                                (() => {
                                  if (show.total_slots_actual) {
                                    return `${show.total_slots_actual} total`;
                                  } else if (show.total_slots_extrapolated) {
                                    return `${show.total_slots_extrapolated} total`;
                                  } else {
                                    const calculated = Math.max(0, (show.assigned_slots || 0) + (show.available_slots || 0));
                                    return `${calculated} total`;
                                  }
                                })()
                              )}
                            </div>
                            <div className="text-gray-500">
                              {showSlots[show.id]
                                ? showSlots[show.id].filter(slot => slot.assigned_brand).length
                                : (show.assigned_slots || 0)
                              } assigned
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getUtilizationColor(show.utilization)}>
                            {show.utilization?.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(show.creator_floor_ecpm)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={show.floor_status === 'clearable' ? 'default' : 'destructive'}
                            className={show.floor_status === 'clearable' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                          >
                            {show.floor_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {show.assigned_brands?.map((brand, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {brand}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Slot Details */}
                      {expandedShows.has(show.id) && (
                        <TableRow>
                          <TableCell colSpan={13} className="bg-gray-50 p-0">
                            <div className="p-4">
                              <h4 className="font-medium mb-2">Slot Details</h4>
                              {showSlots[show.id] ? (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Episode/Scene</TableHead>
                                      <TableHead>Slot Type</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Assigned Brand</TableHead>
                                      <TableHead>Integration Context</TableHead>
                                      <TableHead>Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {showSlots[show.id].map((slot) => (
                                      <TableRow key={slot.id}>
                                        <TableCell>{slot.episode}</TableCell>
                                        <TableCell>
                                          <Badge variant="outline">
                                            {slot.slot_type.replace('_', ' ')}
                                          </Badge>
                                        </TableCell>
                                        <TableCell>
                                          <Badge
                                            variant={slot.status === 'assigned' ? 'default' : 'secondary'}
                                          >
                                            {slot.status}
                                          </Badge>
                                        </TableCell>
                                        <TableCell>{slot.assigned_brand || '-'}</TableCell>
                                        <TableCell className="max-w-md">
                                          <div className="whitespace-normal break-words">
                                            {slot.integration_context || '-'}
                                          </div>
                                        </TableCell>
                                        <TableCell>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(slot.id, 'Slot')}
                                            className="h-6 w-6 p-0 hover:bg-gray-100"
                                            title="Copy Slot ID"
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <div className="flex items-center justify-center py-4">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <span className="ml-2 text-sm text-gray-500">Loading slots...</span>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>
                {listTotal === 0 ? (
                supplyFiltersActive ? 'No shows match your filters.' : 'No shows found.'
              ) : (
                `Showing ${((page - 1) * PAGE_SIZE) + 1} to ${Math.min(page * PAGE_SIZE, listTotal)} of ${listTotal} shows`
              )}
              </span>
              {isFetching && (
                <span className="inline-flex items-center gap-1 text-blue-600">
                  <span className="h-3 w-3 rounded-full border-2 border-blue-600 border-b-transparent animate-spin" />
                  Updating...
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || isFetching}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page * PAGE_SIZE >= listTotal || isFetching}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplyViewPage;